(() => {
  const STORAGE_FALLBACK = 'shoev-promo-code';
  const RESULT_APPLIED = 'applied';
  const RESULT_ADDED = 'added';

  class ShoevPromoCode extends HTMLElement {
    connectedCallback() {
      if (this.dataset.shoevPromoReady === 'true') return;

      this.form = this.querySelector('.shoev-promo__form');
      this.input = this.querySelector('.shoev-promo__input');
      this.button = this.querySelector('.shoev-promo__button');
      this.removeButton = this.querySelector('[data-shoev-promo-remove]');
      this.help = this.querySelector('.shoev-promo__help');
      this.error = this.querySelector('.shoev-promo__message--error');
      this.status = this.querySelector('.shoev-promo__message--saved');
      this.statusCopy = this.querySelector('[data-shoev-promo-status-copy]');
      this.statusValue = this.querySelector('[data-shoev-promo-value]');
      this.statusDetail = this.querySelector('[data-shoev-promo-status-detail]');

      if (!this.form || !this.input || !this.button) return;

      this.dataset.shoevPromoReady = 'true';
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
      this.input.addEventListener('input', () => this.handleInput());
      this.removeButton?.addEventListener('click', () => this.handleRemove());
      this.restoreSavedState();
    }

    get storageKey() {
      return this.dataset.storageKey || STORAGE_FALLBACK;
    }

    readSavedState() {
      try {
        const rawState = window.sessionStorage.getItem(this.storageKey);
        if (!rawState) return null;

        try {
          const parsedState = JSON.parse(rawState);
          if (parsedState && typeof parsedState.code === 'string') return parsedState;
        } catch (_parseError) {
          return { code: rawState, result: RESULT_ADDED };
        }
      } catch (_storageError) {
        return null;
      }

      return null;
    }

    saveState(state) {
      try {
        if (state) window.sessionStorage.setItem(this.storageKey, JSON.stringify(state));
        else window.sessionStorage.removeItem(this.storageKey);
      } catch (_storageError) {
        // The cart update remains authoritative when browser storage is unavailable.
      }
    }

    restoreSavedState() {
      let state = this.readSavedState();
      if (!state?.code) return;

      const normalizedCode = state.code.toLocaleLowerCase();
      const hasAppliedDiscount = Array.from(this.querySelectorAll('[data-shoev-discount-application]')).some(
        (element) =>
          (element.dataset.discountTitle || '').toLocaleLowerCase() === normalizedCode &&
          Number(element.dataset.discountAmount || 0) > 0
      );
      state = { ...state, result: hasAppliedDiscount ? RESULT_APPLIED : RESULT_ADDED };
      this.saveState(state);

      this.input.value = state.code;
      this.showState(state);

      const disclosure = this.querySelector('.shoev-promo__disclosure');
      if (disclosure) disclosure.open = true;
    }

    showState(state) {
      if (!state?.code || !this.status || !this.statusValue || !this.statusCopy) {
        this.clearStateDisplay();
        return;
      }

      const isApplied = state.result === RESULT_APPLIED;
      this.statusCopy.textContent = isApplied ? 'Code applied:' : 'Code added:';
      this.statusValue.textContent = `${state.code}${isApplied ? '' : '.'}`;
      this.statusDetail.textContent = isApplied ? '' : 'Final eligibility is confirmed at checkout.';
      this.statusDetail.hidden = isApplied;
      this.removeButton.hidden = false;
      this.removeButton.setAttribute('aria-label', `Remove promo code ${state.code}`);
      this.status.hidden = false;
      this.help.hidden = true;
    }

    clearStateDisplay() {
      if (this.status) this.status.hidden = true;
      if (this.help) this.help.hidden = false;
      if (this.statusDetail) this.statusDetail.hidden = true;
    }

    handleInput() {
      this.input.removeAttribute('aria-invalid');
      if (this.error) this.error.hidden = true;

      const savedState = this.readSavedState();
      const matchesSavedCode = savedState?.code && this.input.value.trim() === savedState.code;
      if (matchesSavedCode) this.showState(savedState);
      else this.clearStateDisplay();
    }

    handleSubmit(event) {
      event.preventDefault();

      const code = this.input.value.trim();
      if (!code) {
        this.input.setAttribute('aria-invalid', 'true');
        if (this.error) {
          this.error.textContent = 'Enter a promo code to continue.';
          this.error.hidden = false;
        }
        this.clearStateDisplay();
        this.input.focus();
        return;
      }

      this.input.removeAttribute('aria-invalid');
      if (this.error) this.error.hidden = true;
      this.updateDiscount(code, 'apply');
    }

    handleRemove() {
      if (this.error) this.error.hidden = true;
      this.updateDiscount('', 'remove');
    }

    setLoading(isLoading, action = 'apply') {
      const idleLabel = this.button.dataset.idleLabel || 'Apply';
      const loadingLabel = this.button.dataset.loadingLabel || 'Applying...';
      this.button.disabled = isLoading;
      this.button.textContent = isLoading && action === 'apply' ? loadingLabel : idleLabel;
      this.input.readOnly = isLoading;
      this.form.toggleAttribute('aria-busy', isLoading);

      if (this.removeButton) {
        this.removeButton.disabled = isLoading;
        this.removeButton.textContent = isLoading && action === 'remove' ? 'Removing...' : 'Remove';
      }
    }

    getRootUrl() {
      const shopifyRoot = window.Shopify?.routes?.root;
      return (shopifyRoot || this.dataset.rootUrl || '/').replace(/\/?$/, '/');
    }

    getSectionDescriptors() {
      const sections = new Map();
      const addSection = (key, targetSelector, sourceSelector = targetSelector) => {
        if (!key || !document.querySelector(targetSelector)) return;
        sections.set(key, { key, targetSelector, sourceSelector });
      };

      const mainItems = document.getElementById('main-cart-items');
      const mainFooter = document.getElementById('main-cart-footer');

      addSection(mainItems?.dataset.id, '#main-cart-items .js-contents');
      addSection(mainFooter?.dataset.id, '#main-cart-footer .js-contents');
      addSection('cart-drawer', '#CartDrawer .drawer__inner');
      addSection('cart-icon-bubble', '#cart-icon-bubble', '.shopify-section');
      addSection('cart-live-region-text', '#cart-live-region-text', '.shopify-section');

      return Array.from(sections.values());
    }

    renderSections(sectionDescriptors, sectionHtml) {
      const parser = new DOMParser();

      sectionDescriptors.forEach(({ key, targetSelector, sourceSelector }) => {
        const html = sectionHtml?.[key];
        const target = document.querySelector(targetSelector);
        if (!html || !target) return;

        const sourceDocument = parser.parseFromString(html, 'text/html');
        const source = sourceDocument.querySelector(sourceSelector);
        if (!source) return;

        target.innerHTML = source.innerHTML;
        if (target.matches('.drawer__inner')) target.className = source.className;
      });
    }

    responseHasAppliedDiscount(cartState, sectionHtml, code) {
      const normalizedCode = code.toLocaleLowerCase();
      const allocations = [];

      (cartState.cart_level_discount_applications || cartState.discount_applications || []).forEach((application) => {
        allocations.push({ application, amount: application.total_allocated_amount });
      });

      (cartState.items || []).forEach((item) => {
        (item.line_level_discount_allocations || item.discount_allocations || []).forEach((allocation) => {
          allocations.push({
            application: allocation.discount_application || allocation,
            amount: allocation.amount || allocation.total_allocated_amount,
          });
        });
      });

      const jsonShowsDiscount = allocations.some(({ application, amount }) => {
        const title = String(application?.title || application?.code || '').toLocaleLowerCase();
        return title === normalizedCode && Number(amount || 0) > 0;
      });
      if (jsonShowsDiscount) return true;

      return Object.values(sectionHtml || {}).some((html) => {
        if (!html) return false;
        const sourceDocument = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(sourceDocument.querySelectorAll('[data-shoev-discount-application]')).some((element) => {
          const title = (element.dataset.discountTitle || '').toLocaleLowerCase();
          const amount = Number(element.dataset.discountAmount || 0);
          return title === normalizedCode && amount > 0;
        });
      });
    }

    syncAllInstances(state) {
      document.querySelectorAll('shoev-promo-code').forEach((element) => {
        if (!element.input) return;
        if (state?.code) {
          element.input.value = state.code;
          element.showState(state);
          const disclosure = element.querySelector('.shoev-promo__disclosure');
          if (disclosure) disclosure.open = true;
        } else {
          element.input.value = '';
          element.clearStateDisplay();
        }
        element.setLoading(false);
      });
    }

    focusUpdatedControl(action) {
      const selector = this.classList.contains('shoev-promo--drawer')
        ? 'shoev-promo-code.shoev-promo--drawer'
        : 'shoev-promo-code.shoev-promo--page';

      window.requestAnimationFrame(() => {
        const updatedComponent = document.querySelector(selector);
        if (!updatedComponent) return;
        if (action === 'remove') updatedComponent.input?.focus();
        else updatedComponent.removeButton?.focus();
      });
    }

    async updateDiscount(code, action) {
      this.setLoading(true, action);
      const sectionDescriptors = this.getSectionDescriptors();
      const body = {
        discount: code,
        sections: sectionDescriptors.map(({ key }) => key),
        sections_url: window.location.pathname,
      };

      try {
        const response = await fetch(`${this.getRootUrl()}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
        const cartState = await response.json();

        if (!response.ok || cartState.errors) {
          throw new Error(typeof cartState.errors === 'string' ? cartState.errors : 'Unable to update promo code.');
        }

        const state = code
          ? {
              code,
              result: this.responseHasAppliedDiscount(cartState, cartState.sections, code)
                ? RESULT_APPLIED
                : RESULT_ADDED,
            }
          : null;

        this.saveState(state);
        this.renderSections(sectionDescriptors, cartState.sections);
        this.syncAllInstances(state);
        this.focusUpdatedControl(action);
      } catch (_error) {
        this.setLoading(false);
        if (this.error) {
          this.error.textContent = "We couldn't update that code. Please try again.";
          this.error.hidden = false;
        }
      }
    }
  }

  if (!customElements.get('shoev-promo-code')) {
    customElements.define('shoev-promo-code', ShoevPromoCode);
  }
})();
